import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { HelpTooltip } from '@/components/HelpTooltip'
import { cn } from '@/lib/utils'
import { Check, History, Lock } from 'lucide-react'
import type { RevenueBasis, RevenueBasisInfo } from '@/types/shared'

/**
 * Business settings → the account-level REVENUE REPORTING BASIS.
 *
 * Every label and sentence describing a basis is authored by the backend
 * (RevenueCalculator::BASIS_COPY) and rendered verbatim — this component never writes
 * its own description of what gross or net means, so the settings page, the flash
 * confirmation and any reporting badge always say the same thing.
 *
 * NEVER describe net as "ex-VAT" or "excluding VAT". Measured across the estate, most
 * properties deduct nothing at all and the ones that do deduct an amount that varies per
 * booking — it is a PMS deduction, not a tax (docs/revenue-vocabulary.md §8).
 *
 * The change is consequential (it re-states every historical figure), so it is confirmed
 * in a dialog and the consequences are stated on the card itself, not only in the dialog.
 */

interface RevenueBasisCardProps {
  /**
   * `/a/:account_slug/settings` — the business's settings root, served by the backend
   * (`currentAccount.settingsPath`). Every endpoint below is this plus a FIXED suffix.
   */
  settingsPath: string
  current: RevenueBasisInfo
  options: RevenueBasisInfo[]
  /** Owner-only (:manage_account). A display hint — the backend seam is the gate. */
  canManage: boolean
  animationDelay?: number
}

export default function RevenueBasisCard({ settingsPath, current, options, canManage, animationDelay = 0 }: RevenueBasisCardProps) {
  const [selected, setSelected] = useState<RevenueBasis>(current.basis)
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)

  const dirty = selected !== current.basis
  const target = options.find((o) => o.basis === selected) ?? current

  function save() {
    setSaving(true)
    router.patch(
      `${settingsPath}/revenue-basis`,
      { revenue_basis: selected },
      {
        preserveScroll: true,
        onFinish: () => {
          setSaving(false)
          setConfirming(false)
        },
      }
    )
  }

  return (
    <section className="animate-slide-in" style={{ animationDelay: `${animationDelay}ms` }}>
      <div className="rounded-xl border border-border/60 bg-white shadow-sm">
        <div className="flex items-start justify-between gap-3 border-b border-border/40 px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground">Revenue reporting</h2>
              <HelpTooltip content="Which of the two amounts your PMS stores on each booking your revenue figures are based on. This changes what is reported, never what is priced." />
            </div>
            <p className="text-xs text-muted-foreground">
              The basis behind every revenue, ADR and RevPAR figure across this business.
            </p>
          </div>
          <Badge
            variant="outline"
            className="shrink-0 border-primary/30 bg-primary/5 text-[11px] capitalize text-primary"
          >
            {current.label}
          </Badge>
        </div>

        <div className="space-y-4 p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            {options.map((option) => {
              const isSelected = selected === option.basis
              const isCurrent = current.basis === option.basis

              return (
                <label
                  key={option.basis}
                  className={cn(
                    'relative flex cursor-pointer flex-col gap-1.5 rounded-lg border p-4 transition-colors',
                    isSelected
                      ? 'border-primary/60 bg-primary/5 ring-1 ring-primary/30'
                      : 'border-border/60 bg-white hover:border-border',
                    !canManage && 'cursor-default'
                  )}
                >
                  <input
                    type="radio"
                    name="revenue_basis"
                    value={option.basis}
                    checked={isSelected}
                    disabled={!canManage}
                    onChange={() => setSelected(option.basis)}
                    className="sr-only"
                  />
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-semibold text-foreground">{option.headline}</span>
                    <span
                      className={cn(
                        'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors',
                        isSelected ? 'border-primary bg-primary text-white' : 'border-border bg-white'
                      )}
                      aria-hidden="true"
                    >
                      {isSelected && <Check className="h-3 w-3" strokeWidth={3} />}
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed text-muted-foreground">{option.description}</p>
                  {isCurrent && (
                    <span className="mt-1 text-[11px] font-medium uppercase tracking-wide text-primary">In use</span>
                  )}
                </label>
              )
            })}
          </div>

          <div className="rounded-lg border border-border/60 bg-muted/50 p-4">
            <div className="flex items-start gap-2.5">
              <History className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="space-y-1">
                <p className="text-xs font-semibold text-foreground">Changing this re-states your history</p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Figures are rebuilt from your bookings every time a page loads, so past months re-render on the new
                  basis too — a month you read last week will read differently. Comparisons stay like-for-like, because
                  both sides move together.
                </p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  <span className="font-medium text-foreground">Nothing about pricing changes.</span> The base rates,
                  guardrails and targets you set, the rates we compute, and the rates we push to your PMS all stay on
                  the amount the guest pays.
                </p>
              </div>
            </div>
          </div>

          {canManage ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {dirty && (
                <Button variant="ghost" size="sm" className="rounded-lg" onClick={() => setSelected(current.basis)}>
                  Cancel
                </Button>
              )}
              <Button size="sm" className="rounded-lg" disabled={!dirty || saving} onClick={() => setConfirming(true)}>
                {saving ? 'Saving…' : 'Save reporting basis'}
              </Button>
            </div>
          ) : (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5" />
              Only an owner can change the reporting basis.
            </p>
          )}
        </div>
      </div>

      <AlertDialog open={confirming} onOpenChange={(open) => !open && setConfirming(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Report revenue on {target.label.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>
              {target.description} Every revenue, ADR and RevPAR figure in this business — including past months you
              have already looked at — will be re-stated on this basis. The prices you set and the rates we push to
              your PMS are unaffected. You can switch back at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirming(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={save} disabled={saving}>
              {saving ? 'Saving…' : `Report on ${target.label.toLowerCase()}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
